import { useEffect, useState } from 'react'
import axios from '../../api/client'
import { useToast } from '../../context/ToastContext'
import { buildInviteLink } from '../../utils/invite'
import { Button, Modal } from '../common'
import styles from './InviteFriendsModal.module.css'

interface InviteFriendsModalProps {
  open: boolean
  onClose: () => void
}

interface Invite {
  code: string
  used_by: number | null
}

export function InviteFriendsModal({ open, onClose }: InviteFriendsModalProps) {
  const { showToast } = useToast()
  const [inviteLink, setInviteLink] = useState<string | null>(null)
  const [remaining, setRemaining] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!open) return
    setIsLoading(true)
    axios.get('/users/me/invites')
      .then(({ data }) => {
        const available = (data.invites as Invite[] | undefined)?.find(invite => !invite.used_by)
        setInviteLink(available ? buildInviteLink(available.code) : null)
        setRemaining(typeof data.remaining === 'number' ? data.remaining : null)
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [open])

  const handleGenerate = async () => {
    setIsLoading(true)
    try {
      const { data } = await axios.post('/users/me/invite')
      setInviteLink(buildInviteLink(data.code))
      setRemaining(typeof data.remaining === 'number' ? data.remaining : remaining)
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Failed to generate invite link', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopy = async () => {
    if (!inviteLink) return
    try {
      await navigator.clipboard.writeText(inviteLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      showToast('Failed to copy invite link', 'error')
    }
  }

  const handleShare = async () => {
    if (!inviteLink || !navigator.share) return
    try {
      await navigator.share({
        title: 'Join me on tenwords',
        text: 'Here is your invite to tenwords.',
        url: inviteLink,
      })
    } catch (error: any) {
      if (error?.name !== 'AbortError') showToast('Failed to share invite link', 'error')
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Invite a friend" size="sm">
      <div className={styles.content}>
        <p className={styles.description}>Send a friend a direct signup link. Each link can be used once.</p>

        {inviteLink ? (
          <>
            <div className={styles.linkBox}>{inviteLink}</div>
            <div className={styles.actions}>
              <Button variant="primary" icon={`bi ${copied ? 'bi-check2' : 'bi-clipboard'}`} onClick={handleCopy}>
                {copied ? 'Copied' : 'Copy link'}
              </Button>
              {'share' in navigator && (
                <Button variant="secondary" icon="bi bi-send" onClick={handleShare}>
                  Share
                </Button>
              )}
            </div>
          </>
        ) : (
          <Button
            variant="primary"
            icon={`bi ${isLoading ? 'bi-arrow-repeat' : 'bi-link-45deg'}`}
            onClick={handleGenerate}
            disabled={isLoading || remaining === 0}
          >
            {isLoading ? 'Loading...' : remaining === 0 ? 'No invites remaining' : 'Generate invite link'}
          </Button>
        )}

        {remaining !== null && (
          <span className={styles.remaining}>{remaining} invite {remaining === 1 ? 'link' : 'links'} remaining</span>
        )}
      </div>
    </Modal>
  )
}
